import {
  Shield,
  Download,
  MessageSquare,
  Crown,
  Wrench,
  Users,
  Wifi,
  Search,
  Flag,
  Globe,
  RefreshCw
} from 'lucide-react'

const img = (filename) => `/uploads/images/guide/${filename}`

export const GUIDE_MANUAL_GROUPS = [
  {
    title: '入门',
    items: [
      { slug: 'overview', title: '产品概述 / 功能 / 优势', icon: Shield },
      { slug: 'install', title: '安装包安装过程', icon: Download },
      { slug: 'basic-flow', title: '客户端基础运行流程', icon: MessageSquare }
    ]
  },
  {
    title: '进阶',
    items: [
      { slug: 'subscription', title: '余额查询与充值', icon: Crown },
      { slug: 'skills', title: '技能调用', icon: Wrench },
      { slug: 'staff', title: '召唤专家', icon: Users },
      { slug: 'remote', title: '手机远程', icon: Wifi }
    ]
  },
  {
    title: '安全与设置',
    items: [
      { slug: 'security', title: '安全防护', icon: Shield },
      { slug: 'zoom', title: '通用设置', icon: Search }
    ]
  },
  {
    title: '其它',
    items: [
      { slug: 'feedback', title: '意见反馈', icon: Flag },
      { slug: 'website', title: '前往官网', icon: Globe },
      { slug: 'update', title: '版本更新', icon: RefreshCw }
    ]
  }
]

export const GUIDE_MANUAL_PAGES = {
  overview: {
    title: '产品概述 / 功能 / 优势',
    type: 'overview',
    summary:
      'DClaw 是一个开箱即用的桌面端智能助手，围绕“从安装到上手、从对话到任务交付”的完整路径，提供对话、多模型选择、技能插件、专家并行与远程连接能力，让 AI 真正落地到日常工作流中。您不需要准备复杂的开发环境，也不需要自己配置 API Key；安装完成后即可开始使用，并能在需要时逐步扩展更多能力。',
    sections: [
      {
        title: '产品概述',
        bullets: [
          '开箱即用：一键安装，免除复杂环境配置，面向普通用户也能快速上手',
          '桌面端原生体验：更贴近真实办公流程，可自然承接“查看、整理、产出、交付”的操作链路',
          '对话与任务结合：不仅能聊天，更能围绕具体目标组织步骤并持续推进',
          '可扩展能力：技能与连接器按需安装，能力可随着使用深度逐步增强',
          '多端协作：支持通过第三方工具进行远程连接，把对话带到更多场景中'
        ]
      },
      {
        title: '核心功能',
        bullets: [
          '对话与多模型选择：按场景选择更合适的大模型，兼顾速度、成本与效果',
          '连续会话体验：在同一会话中保持上下文，让任务从提出到完成更连贯',
          '技能插件：在专业领域完成更复杂的任务，例如办公、内容生产、数据处理等',
          '专家并行：多角色同时处理任务，适合“并行调研/并行产出/并行验证”的场景',
          '远程连接：通过第三方工具随时随地对话，降低设备与场景限制',
          '会员订阅与积分：按需升级与购买资源包，使用成本更可控',
          '订单与明细：提供积分构成、变更明细与订单列表，便于核对与管理',
          '社区与文档：在讨论区获取经验与帮助，持续学习更高效的用法'
        ]
      },
      {
        title: '优势亮点',
        bullets: [
          '更贴近实际流程：围绕“安装→上手→进阶”的路径设计，用户能按章节逐步掌握全部能力',
          '更强调可控与可核对：提供积分、订单与明细，让使用成本与变更更透明',
          '更强调安全边界：对高风险操作、敏感信息与不可信能力建立防护策略',
          '更适合复杂任务：通过技能与专家并行，将复杂工作拆解并同时推进',
          '更容易规模化使用：团队、专家与远程场景可复用，便于形成稳定的使用方法论'
        ]
      }
    ]
  },
  install: {
    title: 'DClaw 安装包安装过程',
    type: 'steps',
    steps: [
      { image: img('anzhuang-1.png'), text: '打开 DClaw.xx.exe 文件后展示安装窗口' },
      { image: img('anzhuang-2.png'), text: '选择安装目录路径' },
      { image: img('anzhuang-3.png'), text: '安装进度展示' },
      { image: img('anzhuang-4.png'), text: '安装结果展示，安装成功' }
    ]
  },
  'basic-flow': {
    title: 'DClaw 客户端基础运行流程',
    type: 'steps',
    steps: [
      { image: img('work-1.png'), text: '客户端正在打开中，需要等待 2-10 秒进入客户端（根据当前机器配置而定）' },
      { image: img('work-2.png'), text: '客户端打开界面展示' },
      { image: img('work-3.png'), text: '点击登录、或者开始对话时会跳到登录界面' },
      { image: img('work-3-yinsizhengce.png'), text: '通过登录界面可以点击查看“隐私政策”' },
      { image: img('work-3-yonghuxieyi.png'), text: '通过登录界面可以点击查看“用户协议”' },
      { image: img('work-4.png'), text: '查看当前会话可以使用的大模型，挑选适合自己的一款' },
      { image: img('work-5.png'), text: '输入您的对话内容，按回车键' },
      { image: img('work-6.png'), text: '展示您的对话内容和 AI 的会话内容，当前是 AI 正在思考中' },
      { image: img('work-7.png'), text: '展现 AI 返回的全部内容' },
      { image: img('work-8-jifen.png'), text: '展现当前用户的剩余积分' },
      {
        image: img('work-9.png'),
        text: '查看用户对话的token消耗情况；一条对话可能产生多条扣费记录，是大模型处理复杂任务的结果。'
      },
      { image: img('work-9-order.png'), text: '展现当前所有支付订单列表' },
      { image: img('work-9-insurance.png'), text: '可根据支付订单来开具发票，企业可开专票。申请开票后3个工作日内会给您开好发票。' }
    ]
  },
  subscription: {
    title: 'DClaw 余额查询与充值',
    type: 'steps',
    steps: [
      { image: img('buy-1.png'), text: '点击上方“账户充值”展现购买窗口，并点击“立即升级”按钮' },
      { image: img('buy-2.png'), text: '展示当前购买的余额充值套餐和支付方式及付款金额' },
      { image: img('buy-3.png'), text: '手机扫码支付成功后展示“支付成功”' },
      { image: img('buy-4.png'), text: '支付成功后关闭窗口，可以看到右上角积分已增加' },
      { image: img('buy-5.png'), text: '此界面可以查看账户所有的支付明细' },
      { image: img('buy-6.png'), text: '重新进入“查看用量明细”，可以看到新充值的记录' }
    ]
  },
  skills: {
    title: 'DClaw 技能调用',
    type: 'steps',
    lead: '您可以挑选所需技能完成专业领域的任务。',
    steps: [
      { image: img('skill-1.png'), text: '进入技能页面，选择要使用的技能' },
      { image: img('skill-2.png'), text: '打开技能详情窗口，点击安装' },
      { image: img('skill-3.png'), text: '展示安装过程' },
      { image: img('skill-4.png'), text: '技能安装成功，点击去使用' },
      { image: img('skill-5.png'), text: '技能在会话窗口中展示的形式如图，输入内容继续完成任务' },
      { image: img('skill-6.png'), text: '使用技能后的会话展示' }
    ]
  },
  staff: {
    title: 'DClaw 召唤专家',
    type: 'steps',
    lead: '您可以挑选若干个干活的专家为您同时处理任务。',
    steps: [
      { image: img('yuangong-1.png'), text: '进入专家页面' },
      { image: img('yuangong-2.png'), text: '找到适合自身的专家进入详情页，点击“添加”' },
      { image: img('yuangong-3.png'), text: '点击“去使用”' },
      { image: img('yuangong-4.png'), text: '当前使用了该专家，并且可以开始对话' }
    ]
  },
  remote: {
    title: 'DClaw 用手机远程',
    type: 'steps',
    lead: '您可以通过其他工具比如微信、飞书，随时随地来和 DClaw 进行对话。',
    steps: [
      { image: img('remote-1.png'), text: '进入远程连接界面' },
      { image: img('remote-2.png'), text: '选择工具，点击“连接”' },
      { image: img('remote-3.png'), text: '手机打开工具进行扫一扫，扫码后提示“扫码成功，正在连接中”' },
      { image: img('remote-4.jpg'), text: '手机上展示会话窗口，可以开启聊天' }
    ]
  },
  security: {
    title: 'DClaw 安全防护',
    type: 'security',
    image: img('safe.png'),
    tables: [
      {
        title: '电脑环境安全防护',
        rows: [
          {
            point: '高风险行为识别与拦截',
            what:
              '识别系统配置变更、敏感文件访问、可疑脚本执行、提权与持久化等行为，并进行拦截或强约束。',
            compare: '不少产品偏“只对话/只调用”，对本机可执行动作缺少清晰边界。'
          },
          {
            point: '可控执行与风险兜底',
            what:
              '在自动化链路中优先保证边界清晰、风险可控，尽量避免不可逆变化。',
            compare: '常见做法更强调能力覆盖，风险更多依赖用户自行判断。'
          },
          {
            point: '过程控制与中断机制',
            what:
              '执行前识别指令意图与潜在影响，执行中对危险动作限制与中断，避免链路失控。',
            compare: '部分产品缺少执行过程管控，指令一旦开始就难以收回。'
          },
          {
            point: '可回溯与可定位',
            what:
              '尽量保留可回溯线索，便于异常定位来源与影响范围。',
            compare: '不少方案缺少可追踪线索，出问题后更难定位与复盘。'
          }
        ]
      },
      {
        title: '敏感信息识别与保护',
        rows: [
          {
            point: '输入内容安全自检',
            what:
              '对提示词与任务内容做安全识别，检测隐私、密钥、凭证、Token、Cookie 等高风险内容。',
            compare: '不少产品“原样发给模型”，风险暴露后才补救。'
          },
          {
            point: '脱敏与替代建议',
            what:
              '对可疑敏感信息给出提示与建议，引导使用脱敏描述或更安全的配置方式。',
            compare: '常见方案提示不足，用户容易在不知情下直接粘贴敏感内容。'
          },
          {
            point: '面向办公与企业场景',
            what:
              '针对高频办公使用做前置防护，降低误传与误泄露概率。',
            compare: '不少产品偏个人对话场景，企业敏感信息防护覆盖不足。'
          }
        ]
      },
      {
        title: '技能安全扫描机制',
        rows: [
          {
            point: '接入前多层检测',
            what:
              '技能安装/接入前做来源评估、权限检查与风险扫描，避免不可信能力进入本地环境。',
            compare: '常见“插件装上就能跑”，对来源与风险缺少门槛。'
          },
          {
            point: '权限与边界评估',
            what:
              '明确技能能访问什么、需要什么权限、可能产生哪些影响，把扩展能力纳入可控边界。',
            compare: '不少产品只关注功能是否可用，对权限边界描述不清。'
          },
          {
            point: '可管理的扩展体系',
            what:
              '把扩展能力做成可管理能力，便于长期使用、规模化安装与异常定位。',
            compare: '黑盒式扩展更难维护，技能多后稳定性与安全性更不可控。'
          }
        ]
      }
    ]
  },
  zoom: {
    title: 'DClaw 通用设置',
    type: 'steps',
    lead: '为了调整字体大小方便。',
    steps: [
      { image: img('fangdajing-1.png'), text: '点击左下角放大镜图标' },
      { image: img('fangdajing-2.png'), text: '拖拽按钮来调整界面大小' }
    ]
  },
  feedback: {
    title: 'DClaw 意见反馈功能',
    type: 'steps',
    lead: '为了用户更好的体验，我们会持续优化反馈的问题。',
    steps: [
      { image: img('fankui-1.png'), text: '点击左下角反馈按钮' },
      { image: img('fankui-2.png'), text: '展示反馈提交界面' },
      { image: img('fankui-3.png'), text: '提交成功后展示' }
    ]
  },
  website: {
    title: 'DClaw 前往官网流程',
    type: 'steps',
    lead: '为了让用户更好地了解我们。',
    steps: [
      { image: img('guanwang-1.png'), text: '点击左下角“关于DClaw”' },
      { image: img('guanwang-2.png'), text: '查看“关于DClaw”详情' },
      { image: img('guanwang-3.png'), text: '展示官网内容' }
    ]
  },
  update: {
    title: 'DClaw 版本更新流程',
    type: 'steps',
    lead: '为了更好地用户体验，请升级到最新版本的客户端。',
    steps: [
      { image: img('update-0.png'), text: '客户端提示有版本更新' },
      { image: img('update-1.png'), text: '进入版本更新入口' },
      { image: img('update-2.png'), text: '展示当前版本已经是最新版' },
      { image: img('update-3.png'), text: '当前版本不是最新版则显示最新版的版本号' },
      { image: img('update-4.png'), text: '展示最新版本的更新内容' },
      { image: img('update-5.png'), text: '点击更新版本后，进入下载状态，下载好后重启客户端即可' }
    ]
  }
}

export function getManualPage(slug) {
  return GUIDE_MANUAL_PAGES[slug] || null
}
